import { subDays, subHours, subYears } from "date-fns";

const LOOKBACKS = {
  "24h": { label: "Last 24 hours", start: (now: Date) => subHours(now, 24) },
  "7d": { label: "Last 7 days", start: (now: Date) => subDays(now, 7) },
  "30d": { label: "Last 30 days", start: (now: Date) => subDays(now, 30) },
  "90d": { label: "Last 90 days", start: (now: Date) => subDays(now, 90) },
  "1y": { label: "Last year", start: (now: Date) => subYears(now, 1) },
} as const;

export type Lookback = keyof typeof LOOKBACKS;

export const DEFAULT_LOOKBACK: Lookback = "7d";

export const LOOKBACK_LABELS: { label: string; value: Lookback }[] =
  Object.entries(LOOKBACKS).map(([value, { label }]) => ({
    label,
    value: value as Lookback,
  }));

export type TimeRange =
  | { type: "custom"; endTime: Date; startTime: Date }
  | { type: "relative"; lookback: Lookback };

export const DEFAULT_TIME_RANGE: TimeRange = {
  lookback: DEFAULT_LOOKBACK,
  type: "relative",
};

export const isLookback = (value: string | null): value is Lookback =>
  value !== null && Object.hasOwn(LOOKBACKS, value);

export const isDefaultTimeRange = (range: TimeRange) =>
  range.type === "relative" && range.lookback === DEFAULT_LOOKBACK;

/** Resolves to absolute bounds at call time, so a relative range keeps rolling
 * between requests instead of freezing when the filter was applied. */
export const resolveTimeRange = (
  range: TimeRange
): { endTime: string; startTime: string } => {
  if (range.type === "custom")
    return {
      endTime: range.endTime.toISOString(),
      startTime: range.startTime.toISOString(),
    };
  const now = new Date();
  return {
    endTime: now.toISOString(),
    startTime: LOOKBACKS[range.lookback].start(now).toISOString(),
  };
};
